import React, {useEffect, useRef, useState} from 'react'
import {useForm} from 'react-hook-form';
import {useHistory} from 'react-router-dom';
import User from 'src/types/user-type';
import authService from 'src/_services/auth.service';
import {observer} from "mobx-react";
import useStore from "src/store";
import Lang, {getLang, setLang} from "src/assets/lang";
import SplitButton from "src/pages/components/SplitButton";
import styled from "styled-components";
import Spinner from "../components/spinner";
import {Routes} from "../../core/Routes";
import {ClientFlow, ClientType} from "../../types/client-type";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const Root = styled.div`
  form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 150px);
    min-height: 500px;
  }

  @media (max-width: 992px) {
    flex-flow: column-reverse;
    justify-content: flex-end;
    .login-logo-img{
      display: none;
    }
    form{
        justify-content: flex-start;
        padding-top: 24px;
    }
  }
`;

const LoginLogoImg = styled.div`
  background: url(/images/login_logo.webp) center;
  background-size: cover;
  width: 220px;
  height: 70px;
`;

const LoginForm = styled.div`
  height: auto;
  max-width: 450px;
  width: 100%;
`;

const RightBlock = styled.div`
  position: relative;
  margin: 0;
  padding: 0;
  background: url(/images/login_bg.webp) center;
  background-size: cover;
   
  .addition-image{
    position: absolute;
    right: 0; 
    bottom: 0;
    height: 90%;
 }
 .trustpilot-image{
    position: absolute;
    left: 30px; 
    bottom: 15px;
 }
 
 
  @media (max-width: 1360px) {
    .addition-image{
        height: 80%;
    }
  }
  @media (max-width: 1260px) {
    .addition-image{
        height: 60%;
    }
  }
  @media (max-width: 992px) {
    height: 150px;
    .addition-image{
        display: none;
    }
    .trustpilot-image{
      left: auto; 
      bottom: 10px;
      height: 27px;
    }
  }
`;

const LoginBgImg = styled.div`
    padding: 60px;
    background-color: #3498DBA6;
    height: 100vh;
    display: flex;
    flex-direction: column;
 
    @media (max-width: 992px) {
      height: 150px;
      padding: 12px;
      align-items: center;
    }
`;

const LoginHeader = styled.h4`
 max-width: 300px;
 font-weight: 600;
 margin-bottom: 12px;
`;

const OrLogin = styled.div`
 font-weight: 400;
 font-size: 18px;
`;

const LoginSubHeader = styled.h6`

`;

const LoginFieldName = styled.div`
  font-size: 14px;
  font-weight: 600;
  a:-webkit-any-link:focus {
    outline: none;
  }
`

const LoginInput = styled.input`
  background-color: #f3f6f9;
  border: 0;
  padding: 17px;
  border-radius: 12px;
  :focus {
    background-color: #ebedf3;
  }
`
const LoginBtn = styled.button`
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  &.btn-primary:focus {
    box-shadow: none;
  }
  &.btn-primary:not(:disabled):not(.disabled):active:focus {
    box-shadow: none;
  }
  .split-button .color {
    font-size: 10px;
    color: black;
  }
`;

const StyledSplitButton = styled(SplitButton)`
  position: absolute;
  bottom: 10px;
  right: 20px;
  .split-button .color {
    font-size: 10px;
    color: black;
  }
`;


const LogoContainer = styled.div`
 display: flex;
 .logo-image{
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background-color: white;
    @media (max-width: 992px) {
        width: 40px;
        height: 40px;
    }
 }
 .logo-text{
    display: flex;
    align-items: center;
    width: min-content;
    font-size: 18px;
    border-left: 2px solid white;
    padding-left: 8px; 
    margin-left: 8px;
    text-transform: uppercase;
    color: white;
    line-height: 100%;
    text-shadow: rgba(0,0,0,.5) 3px 3px 10px;
     @media (max-width: 992px) {
        font-size: 12px;
     }
 }
`;
const RightTitle = styled.div`
 width: 400px;
 color: white;
 line-height: 140%;
 font-size: 32px;
 font-weight: 700;
 margin: 24px 0;
 text-shadow: rgba(0,0,0,.5) 3px 3px 10px;
 @media (max-width: 992px) {
    font-size: 18px;
    margin: 8px 0 0 0;
    text-align: center;
 }
`;


const useMountEffect = (fun: any) => useEffect(fun, []);

const Login = () => {
    const {userStore} = useStore();
    const { register, handleSubmit, errors } = useForm();
    const [msg, setMsg] = useState<string | null>(null);
    const history = useHistory();
    const [valueLang, setValueLang] = useState(Lang.getLangIndex(getLang()));
    const [loading, setLoading] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);

    const [email, setEmail] = useState('');

    useMountEffect(()=>{
        userStore.logout();
        const queryParams = new URLSearchParams(window.location.search);
        setEmail(queryParams.get('email') || '');
        const token = queryParams.get('temp_access_token');
        if(token){
            setLoading(true);
            authService.login({temp_access_token: token}).then((response: any) => {
                let user = (response.data.user ? response.data.user : response.data) as User;
                userStore.setUser(user);
                history.push("/");
            }).catch((data) => {
                setMsg(Lang.get(data.error) || "");
            }).finally(() => {
                setLoading(false);
            })
        }
    });

    const onSubmit = (data: any) => {
        if(loginLoading) return;
        data.email = data.email.trim();
        if (data.email && data.password) {
            setLoginLoading(true);
            onLogin(data);
        }
    };

    const onLogin = (data: any) => {
        return authService.login(data).then((response: any) => {
            let user = (response.data.user ? response.data.user : response.data) as User;
            userStore.setUser(user);

            if(userStore.isClient) {
                let now = new Date();
                now.setTime(now.getTime() + 12*30*24*60*60*1000);
                document.cookie = `newois=1; domain=canadacis.org; Expires=${now.toUTCString()};`;
            }
            
            if(user.client){
                if([ClientFlow.Flow4, ClientFlow.Flow5, ClientFlow.Flow6].includes(user.client.flow)){
                    history.push(Routes.CLIENT + Routes.DASH);
                }
                else {
                    if(user.email_verified_at && user.client.client_type === ClientType.Lead){
                        history.push(Routes.CLIENT + Routes.ACTIVATE_PROFILE);
                    }
                    else history.push("/");
                }
            }
            else history.push("/");
        }).catch(() => {
            userStore.logout();
            setMsg(Lang.get("Please provide correct email and password") || "");
            setTimeout(() => setMsg(null), 5000);
        }).finally(() => {
            setLoginLoading(false);
        })
    }

    const googleSuccess = (credentialResponse: any) => {
        setLoginLoading(true);
        onLogin({credential: credentialResponse.credential})
    }

    return loading ? <Spinner /> : (
        <Root className={"row m-0 p-0 align-items-center h-100"}>
            <form className="col-lg-6" data-novalidate="novalidate" onSubmit={handleSubmit(onSubmit)}>
                <LoginForm className="row mb-5 login-logo-img">
                    <LoginLogoImg />
                </LoginForm>
                <LoginForm className="row mb-3">
                    <LoginHeader className="w-100">{Lang.get("Log in to your Online Immigration System")}</LoginHeader>
                    <LoginSubHeader>{Lang.get("Get one step closer to your Canadian future!")}</LoginSubHeader>
                </LoginForm>
                <LoginForm className="row mb-4">
                    <LoginFieldName className="w-100 mb-2">{Lang.get("Your Email")}</LoginFieldName>
                    <LoginInput className="w-100"
                                type="text" name="email"
                                data-autocomplete="off"
                                autoComplete="off"
                                value={email}
                                onChange={(v) => {setEmail(v.target.value)}}
                                ref={register({
                                    required: Lang.get("Email is required"),
                                    pattern: {
                                        value: /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*[ ]*$/,
                                        message: Lang.get("Invalid email address") || "",
                                    },
                                })} />
                    <div className="pb-lg-0 pb-5">
                        {errors.email && <span style={{ color: "red" }}>{errors.email.message}</span>}
                    </div>
                </LoginForm>
                <LoginForm className="row mb-4">
                    <LoginFieldName className="d-flex w-100">
                        <label className="w-100">{Lang.get("Password")}</label>
                        <div className={"w-100 d-flex justify-content-end"}>
                            <a href="/forgot-password" onClick={(e) => {e.preventDefault(); history.push('/forgot-password')}}>
                                {Lang.get("Forgot Password?")}
                            </a>
                        </div>
                    </LoginFieldName>
                    <LoginInput className="w-100"
                                type="password"
                                name="password"
                                data-autocomplete="off"
                                ref={register({
                                    required: Lang.get("Password is required"),
                                    minLength: {
                                        value: 6,
                                        message: Lang.get("Min length is 6") || "",
                                    },
                                })} />
                    <div className="pb-lg-0 pb-5">
                        {errors.password && <span style={{ color: "red" }}>{errors.password.message}</span>}
                    </div>
                </LoginForm>
                <LoginForm className="row justify-content-between align-items-center">
                    <LoginBtn type="submit" className={"w-auto btn btn-primary d-flex flex-row align-items-center" + (loginLoading ? " disabled" : "")}>
                        {Lang.get("Log In")}
                        {loginLoading && <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true"></span>}
                    </LoginBtn>
                    <OrLogin>
                        {Lang.get('or')}
                    </OrLogin>
                    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
                        <GoogleLogin
                            onSuccess={googleSuccess}
                            onError={() => {
                                console.log('Login Failed');
                            }}
                        />
                    </GoogleOAuthProvider>

                </LoginForm>
                {msg && <LoginForm className="row pt-1" ><span style={{ color: "red" }}>{msg}</span></LoginForm>}
            </form>
            <RightBlock className={"col-lg-6"}>
                <LoginBgImg>
                    <LogoContainer>
                        <img className={'logo-image'} src={'/images/square-logo.webp'} alt={''} />
                        <div className={'logo-text'}>{Lang.get('ONLINE IMMIGRATION SYSTEM')}</div>
                    </LogoContainer>
                    <RightTitle>
                        <div>{Lang.get('THE LEADING PLATFORM FOR EASY AND SMOOTH IMMIGRATION TO CANADA')}</div>
                    </RightTitle>
                    <img className={'addition-image'} src={'/images/CreatingOISAccount.png'} alt={''} />
                    <img className={'trustpilot-image'} src={'/images/Trustpilot_white.png'} alt={''} />
                </LoginBgImg>
            </RightBlock>
            <StyledSplitButton
                className={"w-auto"}
                onClickItems={(newValue: number) => {
                    if(valueLang === newValue) return;
                    setLang(newValue);
                    setValueLang(newValue);
                    window.location.href = window.location.pathname;
                }}
                disabled={false}
                value={valueLang}
                options={Lang.getLangNames()}
                isLanguage={true}
                alwaysSmall
                revertColorForDesktop
            />
        </Root>
    )
}
export default observer(Login);